import type { EntityOutboxEntry } from '../offline/db'
import { resetDeletedLotLabels } from './deletedLots'
import type { Lot, LotEvent, LotEventHistoryScope, LotLineageProjectionRow, LotLocationType, LotStage } from '../types/lot'
import type {
  LotStoryArtifactBundleV1,
  LotStoryArtifactDetailV1,
  LotStoryDraftCreateRequest,
  LotStoryPreviewRequest,
  LotStoryProjectionV1
} from '../types/lotStory'
import { ensureOfflineReady } from '../offline/initOffline'
import { enqueueEntityChange } from '../offline/outbox/entityOutbox'
import { drainEntityOutboxNow } from '../offline/outbox/syncEntityOutboxNow'
import { getOfflineDb } from '../offline/dbFactory'
import { dexieLotRepo } from '../offline/repos/DexieLotRepo'
import { dexieLotEventRepo } from '../offline/repos/DexieLotEventRepo'
import { readLotsFromLocalStorage } from '../offline/lots/lotsLocalStorageMirror'
import { dexieActivityRepo } from '../offline/repos/DexieActivityRepo'
import {
  api,
  type LotMergeCommandPayload,
  type LotMergeCommandResult,
  type LotMoveCommandPayload,
  type LotMoveCommandResult,
  type LotStoryGroupDeliveryRecord,
  type LotStoryGroupDeliveryReceiptRequest,
  type LotStoryGroupDeliveryReceiptRecord,
  type LotStoryGroupReadPreflightRecord,
  type LotStoryGroupShareReviewPreflightRecord,
  type LotStoryGroupShareIntentUpdateRequest,
  type QueuedLotUpsertPayload,
  type LotTransferCommandPayload,
  type LotTransferCommandResult,
  type LotMoveFishCommandPayload,
  type LotSplitCommandPayload,
  type LotSplitCommandResult,
  type LotStockIntoUnitsPayload,
  type LotStockIntoUnitsResult,
  type QueuedLotMoveCommandPayload,
  type QueuedLotTransferCommandPayload,
  type QueuedLotMergeCommandPayload,
  type QueuedLotSplitCommandPayload
} from './api'
import { createId } from './id'
import { resolveCanonicalLotKey } from './lotIdentity'
import { purgeLocalDataForLot } from './entityCachePurge'
import {
  readEntityBatchSyncStatus,
  retryEntityBatchSyncStatusWithReport,
  type EntityBatchSyncRetryReport,
  type EntityBatchSyncStatus
} from './entityMutationSync'

const clone = <T>(value: T): T => structuredClone(value)

const nowIso = (): string => new Date().toISOString()

const asObjectRecord = (value: unknown): Record<string, unknown> | null => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>
  }
  return null
}

const compactRecord = (value: Record<string, unknown>): Record<string, unknown> | undefined => {
  const entries = Object.entries(value).filter(([, entry]) => entry !== undefined)
  if (entries.length === 0) {
    return undefined
  }
  return Object.fromEntries(entries)
}

const uniqueStrings = (values: string[]): string[] => {
  const seen = new Set<string>()
  const out: string[] = []

  values.forEach((value) => {
    const key = String(value ?? '').trim()
    if (!key || seen.has(key)) return
    seen.add(key)
    out.push(key)
  })

  return out
}

const normalizeLotDuplicateToken = (value: string): string => {
  const trimmed = value.trim()
  if (!trimmed) return ''

  const canonical = resolveCanonicalLotKey(trimmed)
  return canonical === 'unknown-lot' ? trimmed.toLowerCase() : canonical
}

const findCanonicalDuplicateLot = (args: {
  lots: Lot[]
  lotKey: string
  excludeId?: string
}): Lot | undefined => {
  const candidateToken = normalizeLotDuplicateToken(args.lotKey)
  if (!candidateToken) return undefined

  return args.lots.find((lot) => {
    if (args.excludeId && lot.id === args.excludeId) return false
    return normalizeLotDuplicateToken(lot.lotKey ?? '') === candidateToken
  })
}

const normalizeStage = (value: unknown): LotStage => {
  const allowed: LotStage[] = ['Active', 'Inactive', 'Harvested', 'Planned', 'Dormant']
  return allowed.includes(value as LotStage) ? (value as LotStage) : 'Active'
}

const normalizeOptionalString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

const normalizeLocationType = (value: unknown): LotLocationType | undefined => {
  if (value === 'production-unit' || value === 'farm' || value === 'processing-site') {
    return value
  }
  return undefined
}

const resolveCurrentLocation = (candidate: {
  farmId: string
  productionUnitId?: string
  locationType?: LotLocationType
  locationId?: string
}): { locationType: LotLocationType; locationId?: string } => {
  const locationType = normalizeLocationType(candidate.locationType) ?? 'production-unit'
  const explicitLocationId = normalizeOptionalString(candidate.locationId)

  if (locationType === 'farm') {
    return {
      locationType,
      locationId: normalizeOptionalString(candidate.farmId) ?? explicitLocationId
    }
  }

  if (locationType === 'processing-site') {
    return {
      locationType,
      locationId: explicitLocationId ?? normalizeOptionalString(candidate.productionUnitId) ?? normalizeOptionalString(candidate.farmId)
    }
  }

  return {
    locationType: 'production-unit',
    locationId: normalizeOptionalString(candidate.productionUnitId) ?? explicitLocationId ?? normalizeOptionalString(candidate.farmId)
  }
}

const normalizeLot = (candidate: Lot): Lot => {
  const lotKey = normalizeOptionalString(candidate.lotKey) ?? ''
  const farmId = normalizeOptionalString(candidate.farmId) ?? ''
  const productionUnitId = normalizeOptionalString(candidate.productionUnitId)
  const currentLocation = resolveCurrentLocation({
    farmId,
    productionUnitId,
    locationType: candidate.locationType,
    locationId: candidate.locationId
  })

  return {
    ...candidate,
    lotKey,
    lineageRootId: normalizeOptionalString(candidate.lineageRootId) ?? candidate.id,
    sourceLotKey: normalizeOptionalString(candidate.sourceLotKey) ?? (lotKey || undefined),
    farmId,
    productionUnitId,
    locationType: currentLocation.locationType,
    locationId: currentLocation.locationId,
    stage: normalizeStage(candidate.stage),
    species: candidate.species?.trim() ? candidate.species.trim() : undefined,
    metadata: candidate.metadata && typeof candidate.metadata === 'object' && !Array.isArray(candidate.metadata)
      ? clone(candidate.metadata)
      : undefined
  }
}

export type LotCreatePayload = Omit<Lot, 'id' | 'createdAt' | 'updatedAt'> & {
  id?: string
}
export type LotMutationSyncState = 'offline' | 'pending' | 'synced'
export type LotMutationResult<T> = {
  data: T
  syncState: LotMutationSyncState
  syncPromise: Promise<LotMutationSyncState> | null
}

type LotListParams = {
  farmId?: string
  productionUnitId?: string
  lotKey?: string
  stage?: string
}

const isOnline = (): boolean => typeof navigator !== 'undefined' && navigator.onLine

const sortLots = (lots: Lot[]): Lot[] => (
  lots
    .slice()
    .sort((a, b) => {
      const keyCompare = (a.lotKey ?? '').localeCompare(b.lotKey ?? '')
      if (keyCompare !== 0) return keyCompare
      return (b.updatedAt ?? '').localeCompare(a.updatedAt ?? '')
    })
)

const buildLotSnapshotMetadata = (lot: Lot): Record<string, unknown> | undefined => compactRecord({
  lotKey: normalizeOptionalString(lot.lotKey),
  stage: lot.stage,
  farmId: normalizeOptionalString(lot.farmId),
  productionUnitId: normalizeOptionalString(lot.productionUnitId),
  species: normalizeOptionalString(lot.species),
  biomassKg: typeof lot.biomassKg === 'number' ? lot.biomassKg : undefined
})

const readQueuedLocalLedgerEvent = (payload: unknown): LotEvent | undefined => {
  const record = asObjectRecord(payload)
  const event = asObjectRecord(record?.localLedgerEvent)
  if (!event) return undefined
  if (typeof event.id !== 'string' || event.id.trim().length === 0) return undefined
  if (event.eventType !== 'create' && event.eventType !== 'update') return undefined
  if (typeof event.recordedAt !== 'string' || event.recordedAt.trim().length === 0) return undefined
  return event as unknown as LotEvent
}

const readPendingLotUpsertPayload = async (lotId: string): Promise<QueuedLotUpsertPayload | undefined> => {
  const entries = await getOfflineDb().entityOutbox.toArray()
  const entry = entries.find((candidate) => candidate.entityType === 'lot' && candidate.entityId === lotId && candidate.op === 'upsert')
  if (!entry) return undefined

  const payload = asObjectRecord(entry.payload)
  if (!payload) return undefined
  return payload as QueuedLotUpsertPayload
}

const buildProjectedCreateLotEvent = (lot: Lot, eventId: string): LotEvent => ({
  id: eventId,
  eventType: 'create',
  recordedAt: lot.createdAt ?? lot.updatedAt ?? nowIso(),
  lineageRootId: lot.lineageRootId ?? lot.id,
  sourceLotIds: [],
  targetLotIds: [lot.id],
  toLocationType: lot.locationType,
  toLocationId: lot.locationId,
  metadata: compactRecord({
    toFarmId: lot.farmId,
    toProductionUnitId: lot.productionUnitId,
    after: buildLotSnapshotMetadata(lot)
  })
})

const buildProjectedUpdateLotEvent = (params: {
  beforeLot: Lot
  afterLot: Lot
  eventId: string
  baseEvent?: LotEvent
}): LotEvent => {
  const baseMetadata = asObjectRecord(params.baseEvent?.metadata)

  return {
    id: params.eventId,
    eventType: 'update',
    recordedAt: params.afterLot.updatedAt ?? nowIso(),
    lineageRootId: params.afterLot.lineageRootId ?? params.afterLot.id,
    sourceLotIds: params.baseEvent?.sourceLotIds?.length ? uniqueStrings(params.baseEvent.sourceLotIds) : [params.beforeLot.id],
    targetLotIds: [params.afterLot.id],
    fromLocationType: params.baseEvent?.eventType === 'update'
      ? params.baseEvent.fromLocationType
      : params.beforeLot.locationType,
    fromLocationId: params.baseEvent?.eventType === 'update'
      ? params.baseEvent.fromLocationId
      : params.beforeLot.locationId,
    toLocationType: params.afterLot.locationType,
    toLocationId: params.afterLot.locationId,
    metadata: compactRecord({
      fromFarmId: typeof baseMetadata?.fromFarmId === 'string' ? baseMetadata.fromFarmId : params.beforeLot.farmId,
      toFarmId: params.afterLot.farmId,
      fromProductionUnitId: typeof baseMetadata?.fromProductionUnitId === 'string'
        ? baseMetadata.fromProductionUnitId
        : params.beforeLot.productionUnitId,
      toProductionUnitId: params.afterLot.productionUnitId,
      before: asObjectRecord(baseMetadata?.before) ?? buildLotSnapshotMetadata(params.beforeLot),
      after: buildLotSnapshotMetadata(params.afterLot)
    })
  }
}

const buildQueuedLotUpsertProjection = async (params: {
  nextLot: Lot
  previousLot?: Lot
  preferredEventType: 'create' | 'update'
}): Promise<{ payload: QueuedLotUpsertPayload; projectedEvent: LotEvent }> => {
  const pendingPayload = await readPendingLotUpsertPayload(params.nextLot.id)
  const pendingEvent = readQueuedLocalLedgerEvent(pendingPayload)

  const projectedEvent = pendingEvent?.eventType === 'create' || params.preferredEventType === 'create'
    ? buildProjectedCreateLotEvent(params.nextLot, pendingEvent?.id ?? createId('lineage'))
    : buildProjectedUpdateLotEvent({
      beforeLot: params.previousLot ?? params.nextLot,
      afterLot: params.nextLot,
      eventId: pendingEvent?.id ?? createId('lineage'),
      baseEvent: pendingEvent?.eventType === 'update' ? pendingEvent : undefined
    })

  return {
    payload: {
      ...params.nextLot,
      id: params.nextLot.id,
      localLedgerEvent: projectedEvent
    },
    projectedEvent
  }
}

const projectLocalLotEvents = async (events: LotEvent[]): Promise<void> => {
  if (events.length === 0) return
  await dexieLotEventRepo.upsertMany(events)
}

const pendingLotIdsFromEntry = (entry: EntityOutboxEntry): string[] => {
  if (entry.entityType === 'lot') {
    return [entry.entityId]
  }

  const payload = asObjectRecord(entry.payload)
  if (!payload) return []

  if (entry.entityType === 'lotMoveCommand') {
    const sourceLotId = normalizeOptionalString(payload.sourceLotId)
    return sourceLotId ? [sourceLotId] : []
  }

  if (entry.entityType === 'lotTransferCommand') {
    const sourceLotId = normalizeOptionalString(payload.sourceLotId)
    return sourceLotId ? [sourceLotId] : []
  }

  if (entry.entityType === 'lotSplitCommand') {
    const sourceLotId = normalizeOptionalString(payload.sourceLotId)
    const childLotIds = Array.isArray(payload.childLotIds)
      ? payload.childLotIds.filter((value): value is string => typeof value === 'string')
      : []

    return uniqueStrings([sourceLotId ?? '', ...childLotIds])
  }

  if (entry.entityType === 'lotMergeCommand') {
    const sourceLotIds = Array.isArray(payload.sourceLotIds)
      ? payload.sourceLotIds.filter((value): value is string => typeof value === 'string')
      : []
    const targetLotId = normalizeOptionalString(payload.targetLotId)

    return uniqueStrings([...sourceLotIds, targetLotId ?? ''])
  }

  return []
}

const getPendingLotIds = async (): Promise<Set<string>> => {
  const entries = await getOfflineDb().entityOutbox.toArray()
  return new Set(entries.flatMap((entry) => pendingLotIdsFromEntry(entry)))
}

const LINEAGE_COMMAND_ENTITY_TYPES = new Set<EntityOutboxEntry['entityType']>([
  'lotMoveCommand',
  'lotTransferCommand',
  'lotSplitCommand',
  'lotMergeCommand'
])

const getPendingLineageCommandLotIds = async (): Promise<Set<string>> => {
  const entries = await getOfflineDb().entityOutbox.toArray()
  return new Set(entries
    .filter((entry) => LINEAGE_COMMAND_ENTITY_TYPES.has(entry.entityType))
    .flatMap((entry) => pendingLotIdsFromEntry(entry)))
}

export const hasPendingLineageSyncForLineageRoot = async (lineageRootId: string): Promise<boolean> => {
  await ensureOfflineReady()

  const normalizedLineageRootId = normalizeOptionalString(lineageRootId)
  if (!normalizedLineageRootId) {
    return false
  }

  const [lots, pendingLotIds] = await Promise.all([
    dexieLotRepo.list(),
    getPendingLineageCommandLotIds()
  ])

  if (pendingLotIds.size === 0) {
    return false
  }

  return lots
    .map((lot) => normalizeLot(lot))
    .some((lot) => lot.lineageRootId === normalizedLineageRootId && pendingLotIds.has(lot.id))
}

export const hasPendingLotOutboxChanges = async (): Promise<boolean> => {
  await ensureOfflineReady()
  const entries = await getOfflineDb().entityOutbox.toArray()
  return entries.some((entry) => pendingLotIdsFromEntry(entry).length > 0)
}

const readLocalLots = async (params?: LotListParams): Promise<Lot[]> => {
  const all = (await dexieLotRepo.list({
    farmId: params?.farmId,
    productionUnitId: params?.productionUnitId,
    lotKey: params?.lotKey,
    stage: params?.stage
  })).map((lot) => normalizeLot(lot))

  return sortLots(all)
}

const readLocalLotEvents = async (
  id: string,
  params?: { scope?: LotEventHistoryScope }
): Promise<LotEvent[]> => {
  const lot = await dexieLotRepo.getById(id)

  return dexieLotEventRepo.list({
    lotId: id,
    scope: params?.scope,
    lineageRootId: lot?.lineageRootId ?? lot?.id
  })
}

const readLocalLotEventsForLots = async (lotIds: string[]): Promise<LotEvent[]> => {
  return dexieLotEventRepo.listForLotIds(lotIds)
}

const warmMissingLotEventsForLotsFromApi = async (lotIds: string[]): Promise<void> => {
  const requestedLotIds = uniqueStrings(lotIds)
  if (requestedLotIds.length === 0 || !isOnline()) return

  const cachedLotIdSet = new Set(await dexieLotEventRepo.listCachedLotIds(requestedLotIds))
  const uncachedLotIds = requestedLotIds.filter((lotId) => !cachedLotIdSet.has(lotId))
  if (uncachedLotIds.length === 0) return

  const refreshResults = await Promise.allSettled(
    uncachedLotIds.map(async (lotId) => ({
      lotId,
      events: await api.lots.listEvents(lotId, { scope: 'lineage' })
    }))
  )

  const refreshedEvents: LotEvent[] = []
  const emptyLotIds: string[] = []
  refreshResults.forEach((result, index) => {
    if (result.status === 'fulfilled') {
      if (result.value.events.length === 0) {
        emptyLotIds.push(result.value.lotId)
      }
      refreshedEvents.push(...result.value.events)
      return
    }

    console.warn('[lotApi] failed to warm visible lot history', result.reason)
  })

  if (refreshedEvents.length > 0) {
    await dexieLotEventRepo.upsertMany(refreshedEvents)
  }

  if (emptyLotIds.length > 0) {
    await dexieLotEventRepo.markLineageHistoryEmpty(emptyLotIds)
  }
}

const refreshScopedLotEventCache = async (lots: Lot[], pendingLotIds: Set<string>): Promise<void> => {
  const refreshableLots = lots.filter((lot) => !pendingLotIds.has(lot.id))
  if (refreshableLots.length === 0) return

  const cachedLotIdSet = new Set(await dexieLotEventRepo.listCachedLotIds(refreshableLots.map((lot) => lot.id)))
  if (cachedLotIdSet.size === 0) return

  const refreshResults = await Promise.allSettled(
    refreshableLots
      .filter((lot) => cachedLotIdSet.has(lot.id))
      .map(async (lot) => ({
        lotId: lot.id,
        events: await api.lots.listEvents(lot.id, { scope: 'lineage' })
      }))
  )

  const refreshedEvents: LotEvent[] = []
  refreshResults.forEach((result) => {
    if (result.status === 'fulfilled') {
      refreshedEvents.push(...result.value.events)
      return
    }

    console.warn('[lotApi] failed to refresh scoped cached lot history', result.reason)
  })

  await dexieLotEventRepo.upsertMany(refreshedEvents)
}

const pruneUnreachableLotEventCache = async (): Promise<void> => {
  await dexieLotEventRepo.pruneUnreachable(await dexieLotRepo.list())
}

const readLotSyncState = async (lotId: string): Promise<LotMutationSyncState> => {
  return (await getPendingLotIds()).has(lotId) ? 'pending' : 'synced'
}

const startLotSyncAttempt = (lotId: string): {
  syncState: LotMutationSyncState
  syncPromise: Promise<LotMutationSyncState> | null
} => {
  if (!isOnline()) {
    return {
      syncState: 'offline',
      syncPromise: null
    }
  }

  const syncPromise = (async (): Promise<LotMutationSyncState> => {
    try {
      await drainEntityOutboxNow({ maxEntries: 25, maxPasses: 5 })
    } catch (error) {
      console.warn('[lotApi] failed to drain entity outbox after lot mutation', error)
    }

    return readLotSyncState(lotId)
  })()

  return {
    syncState: 'pending',
    syncPromise
  }
}

const buildLotMutationResult = <T>(data: T, lotId: string): LotMutationResult<T> => ({
  data,
  ...startLotSyncAttempt(lotId)
})

const buildSyncedLotMutationResult = <T>(data: T): LotMutationResult<T> => ({
  data,
  syncState: 'synced',
  syncPromise: null
})

const triggerLineageCommandDrain = () => {
  if (!isOnline()) return

  void drainEntityOutboxNow({ maxEntries: 25, maxPasses: 5 }).catch((error) => {
    console.warn('[lotApi] failed to drain entity outbox after lineage command', error)
  })
}

export const materializeLocalLotSnapshot = (
  payload: LotCreatePayload & { id: string; createdAt?: string; updatedAt?: string }
): Lot => {
  const createdAt = payload.createdAt ?? nowIso()
  const updatedAt = payload.updatedAt ?? createdAt

  return normalizeLot({
    ...payload,
    id: payload.id,
    sourceLotKey: payload.sourceLotKey ?? payload.lotKey,
    stage: normalizeStage(payload.stage),
    createdAt,
    updatedAt
  })
}

export const materializeLocalLotUpdate = (
  existing: Lot,
  patch: Partial<Omit<Lot, 'id'>>
): Lot => normalizeLot({
  ...existing,
  ...patch,
  lineageRootId: typeof patch.lineageRootId === 'string' ? patch.lineageRootId : existing.lineageRootId ?? existing.id,
  sourceLotKey: typeof patch.sourceLotKey === 'string' ? patch.sourceLotKey : existing.sourceLotKey ?? existing.lotKey,
  id: existing.id,
  createdAt: existing.createdAt,
  updatedAt: patch.updatedAt ?? nowIso()
})

const queueLotLineageCommand = async (params: {
  entityType: 'lotMoveCommand' | 'lotTransferCommand' | 'lotSplitCommand' | 'lotMergeCommand'
  entityId: string
  payload: QueuedLotMoveCommandPayload | QueuedLotTransferCommandPayload | QueuedLotSplitCommandPayload | QueuedLotMergeCommandPayload
  lots: Lot[]
  events?: LotEvent[]
}): Promise<void> => {
  await ensureOfflineReady()
  await dexieLotRepo.upsertMany(params.lots)
  await projectLocalLotEvents(params.events ?? [])
  await enqueueEntityChange({
    entityType: params.entityType,
    op: 'upsert',
    entityId: params.entityId,
    payload: params.payload
  })
  triggerLineageCommandDrain()
}

export const queueSplitCommandProjection = async (params: {
  payload: QueuedLotSplitCommandPayload
  createdLots: Lot[]
  updatedSourceLot: Lot
  events?: LotEvent[]
}): Promise<void> => {
  await queueLotLineageCommand({
    entityType: 'lotSplitCommand',
    entityId: params.payload.eventId,
    payload: params.payload,
    lots: [params.updatedSourceLot, ...params.createdLots],
    events: params.events
  })
}

export const queueMoveCommandProjection = async (params: {
  payload: QueuedLotMoveCommandPayload
  movedLot: Lot
  events?: LotEvent[]
}): Promise<LotMutationResult<Lot>> => {
  await queueLotLineageCommand({
    entityType: 'lotMoveCommand',
    entityId: params.payload.eventId,
    payload: params.payload,
    lots: [params.movedLot],
    events: params.events
  })

  return buildLotMutationResult(params.movedLot, params.movedLot.id)
}

export const queueTransferCommandProjection = async (params: {
  payload: QueuedLotTransferCommandPayload
  transferredLot: Lot
  events?: LotEvent[]
}): Promise<LotMutationResult<Lot>> => {
  await queueLotLineageCommand({
    entityType: 'lotTransferCommand',
    entityId: params.payload.eventId,
    payload: params.payload,
    lots: [params.transferredLot],
    events: params.events
  })

  return buildLotMutationResult(params.transferredLot, params.transferredLot.id)
}

export const queueMergeCommandProjection = async (params: {
  payload: QueuedLotMergeCommandPayload
  targetLot: Lot
  updatedSourceLots: Lot[]
  events?: LotEvent[]
}): Promise<void> => {
  await queueLotLineageCommand({
    entityType: 'lotMergeCommand',
    entityId: params.payload.eventId,
    payload: params.payload,
    lots: [params.targetLot, ...params.updatedSourceLots],
    events: params.events
  })
}

export const refreshLotsFromApi = async (params?: {
  farmId?: string
  productionUnitId?: string
  lotKey?: string
  stage?: string
}): Promise<Lot[]> => {
  await ensureOfflineReady()

  const [remoteLots, pendingLotIds] = await Promise.all([
    api.lots.list({ ...params, noCache: true }),
    getPendingLotIds()
  ])

  const mergeableLots = remoteLots
    .map((lot: Lot) => normalizeLot(lot))
    .filter((lot: Lot) => !pendingLotIds.has(lot.id))

  await dexieLotRepo.upsertMany(mergeableLots)
  const scopedLocalLots = await dexieLotRepo.list(params)
  const remoteLotIds = new Set(mergeableLots.map((lot) => lot.id))
  const staleLocalLotIds = scopedLocalLots
    .filter((lot) => !pendingLotIds.has(lot.id) && !remoteLotIds.has(lot.id))
    .map((lot) => lot.id)

  if (staleLocalLotIds.length > 0) {
    await getOfflineDb().lots.bulkDelete(staleLocalLotIds)
  }

  const nextScopedLots = await readLocalLots(params)
  await refreshScopedLotEventCache(nextScopedLots, pendingLotIds)
  await pruneUnreachableLotEventCache()

  return nextScopedLots
}

export const refreshLotEventsFromApi = async (
  id: string,
  params?: { scope?: LotEventHistoryScope }
): Promise<LotEvent[]> => {
  await ensureOfflineReady()

  const remoteEvents = await api.lots.listEvents(id, params)
  if (remoteEvents.length > 0) {
    await dexieLotEventRepo.upsertMany(remoteEvents)
  } else if ((params?.scope ?? 'lineage') === 'lineage') {
    await dexieLotEventRepo.markLineageHistoryEmpty([id])
  }
  return readLocalLotEvents(id, params)
}

export const lotApi = {
  list: async (
    params?: LotListParams,
    options?: { preferLocal?: boolean }
  ): Promise<Lot[]> => {
    await ensureOfflineReady()

    // Ensure legacy lots payload is available for portability/export.
    const legacy = readLotsFromLocalStorage()
    if (legacy.length > 0) {
      // If Dexie is empty, initOffline will have migrated already; but in case of partial states,
      // don't lose data.
      const existingCount = await getOfflineDb().lots.count()
      if (existingCount === 0) {
        await dexieLotRepo.upsertMany(legacy.map((lot) => normalizeLot(lot)))
      }
    }

    if (!options?.preferLocal && isOnline()) {
      try {
        return await refreshLotsFromApi(params)
      } catch (error) {
        console.warn('[lotApi] failed to refresh lots from API', error)
      }
    }

    return readLocalLots(params)
  },

  listEvents: async (id: string, params?: { scope?: LotEventHistoryScope }): Promise<LotEvent[]> => {
    await ensureOfflineReady()

    const online = isOnline()

    if (online) {
      try {
        const remoteEvents = await refreshLotEventsFromApi(id, params)
        if (remoteEvents.length > 0) {
          return remoteEvents
        }
      } catch (error) {
        console.warn('[lotApi] failed to refresh lot history from API', error)
      }
    }

    return readLocalLotEvents(id, params)
  },

  listLineageEventsForLots: async (lotIds: string[]): Promise<LotEvent[]> => {
    await ensureOfflineReady()

    if (isOnline()) {
      try {
        await warmMissingLotEventsForLotsFromApi(lotIds)
      } catch (error) {
        console.warn('[lotApi] failed to warm visible lot history from API', error)
      }
    }

    return readLocalLotEventsForLots(lotIds)
  },

  /**
   * Fase 3 roster enrichment (tier-a-merge-consumer-mapping.md §2.6): every
   * `LotLineageProjection` row for a farm, used to fold ancestor lot ids into
   * `buildLotsByFarmAndKey` / `canonicalLotIdByToken`. Fails open — offline or
   * a request error returns `[]`, which reproduces pre-Fase-3 behavior for the
   * roster (no ancestor enrichment) rather than breaking the screen. Not
   * cached in Dexie: this is a read-time enrichment of the roster, not an
   * entity these screens own or sync.
   */
  lineageForFarm: async (farmId: string): Promise<LotLineageProjectionRow[]> => {
    const trimmed = farmId.trim()
    if (!trimmed || !isOnline()) return []

    try {
      return await api.lots.lineage(trimmed)
    } catch (error) {
      console.warn('[lotApi] failed to fetch lot lineage projection', error)
      return []
    }
  },

  /**
   * Same projection, keyed by lot id instead of farm -- a `self`-relation row
   * per location segment, i.e. every farm/production-unit a lot has ever
   * occupied (LotLineageProjectionWriter composes `resolveAncestry` with
   * `resolveHistoricalLocations`). Used to find a transferred lot's *other*
   * locations so a continuity computation can also pull its pre-transfer
   * data in. Fails open the same way `lineageForFarm` does.
   */
  lineageForLots: async (lotIds: readonly string[]): Promise<LotLineageProjectionRow[]> => {
    const trimmed = [...new Set(lotIds.map((id) => id.trim()).filter(Boolean))]
    if (trimmed.length === 0 || !isOnline()) return []

    try {
      return await api.lots.lineageForLots(trimmed)
    } catch (error) {
      console.warn('[lotApi] failed to fetch lot lineage projection by lot id', error)
      return []
    }
  },

  previewStory: async (payload: LotStoryPreviewRequest): Promise<LotStoryProjectionV1> => {
    if (!isOnline()) {
      throw new Error('Lot story preview requires an online connection.')
    }

    const pendingLineageSync = payload.pendingLineageSync ?? await hasPendingLineageSyncForLineageRoot(payload.lineageRootId)
    return api.lotStories.preview({
      ...payload,
      pendingLineageSync
    })
  },

  listStoryArtifacts: async (params?: { lineageRootId?: string }): Promise<LotStoryArtifactBundleV1[]> => {
    if (!isOnline()) {
      throw new Error('Lot story library requires an online connection.')
    }

    return api.lotStories.list(params)
  },

  getStoryArtifact: async (artifactId: string): Promise<LotStoryArtifactDetailV1> => {
    if (!isOnline()) {
      throw new Error('Lot story detail requires an online connection.')
    }

    return api.lotStories.get(artifactId)
  },

  getStoryGroupDelivery: async (artifactId: string): Promise<LotStoryGroupDeliveryRecord> => {
    if (!isOnline()) {
      throw new Error('Lot story GROUP delivery snapshot requires an online connection.')
    }

    return api.lotStories.getGroupDelivery(artifactId)
  },

  getStoryGroupDeliveryReceipt: async (
    artifactId: string,
    options?: LotStoryGroupDeliveryReceiptRequest
  ): Promise<LotStoryGroupDeliveryReceiptRecord> => {
    if (!isOnline()) {
      throw new Error('Lot story GROUP delivery receipt requires an online connection.')
    }

    return api.lotStories.getGroupDeliveryReceipt(artifactId, options)
  },

  getStoryGroupReadPreflight: async (artifactId: string, groupId?: string): Promise<LotStoryGroupReadPreflightRecord> => {
    if (!isOnline()) {
      throw new Error('Lot story GROUP read preflight requires an online connection.')
    }

    return api.lotStories.getGroupReadPreflight(artifactId, groupId)
  },

  getStoryGroupShareReviewPreflight: async (artifactId: string, groupId?: string): Promise<LotStoryGroupShareReviewPreflightRecord> => {
    if (!isOnline()) {
      throw new Error('Lot story GROUP share review preflight requires an online connection.')
    }

    return api.lotStories.getGroupShareReviewPreflight(artifactId, groupId)
  },

  recordStoryGroupShareReview: async (artifactId: string): Promise<LotStoryGroupShareReviewPreflightRecord> => {
    if (!isOnline()) {
      throw new Error('Lot story GROUP share review requires an online connection.')
    }

    return api.lotStories.recordGroupShareReview(artifactId)
  },

  recordStoryGroupShareApproval: async (artifactId: string): Promise<LotStoryGroupShareReviewPreflightRecord> => {
    if (!isOnline()) {
      throw new Error('Lot story GROUP share approval requires an online connection.')
    }

    return api.lotStories.recordGroupShareApproval(artifactId)
  },

  recordStoryGroupShareReleaseGate: async (artifactId: string): Promise<LotStoryGroupShareReviewPreflightRecord> => {
    if (!isOnline()) {
      throw new Error('Lot story GROUP share release gate requires an online connection.')
    }

    return api.lotStories.recordGroupShareReleaseGate(artifactId)
  },

  promoteStoryToGroup: async (artifactId: string): Promise<LotStoryGroupShareReviewPreflightRecord> => {
    if (!isOnline()) {
      throw new Error('Lot story promote-to-GROUP requires an online connection.')
    }

    return api.lotStories.promoteToGroup(artifactId)
  },

  updateStoryGroupShareIntent: async (
    artifactId: string,
    payload: LotStoryGroupShareIntentUpdateRequest
  ): Promise<LotStoryArtifactDetailV1> => {
    if (!isOnline()) {
      throw new Error('Lot story target-group save requires an online connection.')
    }

    return api.lotStories.updateGroupShareIntent(artifactId, payload)
  },

  createStoryDraft: async (payload: LotStoryDraftCreateRequest): Promise<LotStoryArtifactBundleV1> => {
    if (!isOnline()) {
      throw new Error('Lot story draft save requires an online connection.')
    }

    const pendingLineageSync = payload.pendingLineageSync ?? await hasPendingLineageSyncForLineageRoot(payload.lineageRootId)
    return api.lotStories.createDraft({
      ...payload,
      pendingLineageSync
    })
  },

  publishStoryDraft: async (artifactId: string): Promise<LotStoryArtifactBundleV1> => {
    if (!isOnline()) {
      throw new Error('Lot story publish requires an online connection.')
    }

    return api.lotStories.publishDraft(artifactId)
  },

  revokePublishedStory: async (artifactId: string): Promise<LotStoryArtifactBundleV1> => {
    if (!isOnline()) {
      throw new Error('Lot story revoke requires an online connection.')
    }

    return api.lotStories.revokePublished(artifactId)
  },

  readLotSyncStatus: async (lotIds: string[]): Promise<EntityBatchSyncStatus> => {
    await ensureOfflineReady()
    return readEntityBatchSyncStatus('lot', lotIds)
  },

  retryLotSyncStatusWithReport: async (lotIds: string[]): Promise<EntityBatchSyncRetryReport> => {
    await ensureOfflineReady()
    return retryEntityBatchSyncStatusWithReport({ entityType: 'lot', entityIds: lotIds })
  },

  create: async (payload: LotCreatePayload): Promise<LotMutationResult<Lot>> => {
    await ensureOfflineReady()

    const lotKey = payload.lotKey?.trim()
    const explicitLotId = normalizeOptionalString(payload.id)
    if (!lotKey) {
      throw new Error('Lot key is required')
    }
    if (!payload.farmId) {
      throw new Error('Facility is required')
    }
    if (!payload.productionUnitId) {
      throw new Error('Production unit is required')
    }

    // Guard: reject duplicate lotKey within the same farm + production unit.
    const existing = await dexieLotRepo.list({
      farmId: payload.farmId,
      productionUnitId: payload.productionUnitId
    })
    const duplicate = findCanonicalDuplicateLot({ lots: existing, lotKey })
    if (duplicate) {
      throw new Error(
        `A lot named "${lotKey}" already exists in this production unit. Each lot must have a unique name.`
      )
    }

    const timestamp = nowIso()
    const lot: Lot = normalizeLot({
      ...payload,
      id: explicitLotId ?? createId('lot'),
      lotKey,
      lineageRootId: payload.lineageRootId,
      sourceLotKey: payload.sourceLotKey ?? lotKey,
      locationType: payload.locationType,
      locationId: payload.locationId,
      stage: normalizeStage(payload.stage),
      createdAt: timestamp,
      updatedAt: timestamp
    })
    const { payload: queuedPayload, projectedEvent } = await buildQueuedLotUpsertProjection({
      nextLot: lot,
      preferredEventType: 'create'
    })

    await dexieLotRepo.upsert(lot)
    await projectLocalLotEvents([projectedEvent])
    await enqueueEntityChange({
      entityType: 'lot',
      op: 'upsert',
      entityId: lot.id,
      payload: queuedPayload
    })

    void dexieActivityRepo.log({
      source: 'outbox',
      level: 'success',
      title: `Lot saved: ${lot.lotKey}`,
      detail: 'Sync queued',
      farmId: lot.farmId,
      productionUnitId: lot.productionUnitId,
      lotId: lot.id,
      dedupeKey: `outbox:lot:upsert:${lot.id}`,
      dedupeWindowMs: 30_000
    }).catch(() => {})

    return buildLotMutationResult(lot, lot.id)
  },

  moveRemote: async (id: string, payload: LotMoveCommandPayload): Promise<LotMutationResult<Lot>> => {
    await ensureOfflineReady()

    const remoteResult: LotMoveCommandResult = await api.lots.move(id, payload)
    const movedLot = normalizeLot(remoteResult.movedLot)

    await dexieLotRepo.upsert(movedLot)
    await projectLocalLotEvents(remoteResult.events ?? [])
    return buildSyncedLotMutationResult(movedLot)
  },

  /** Findings #197: online only — brings a deleted lot back where it was. */
  restoreRemote: async (id: string): Promise<Lot> => {
    await ensureOfflineReady()

    const restored = normalizeLot(await api.lots.restore(id))
    await dexieLotRepo.upsert(restored)
    resetDeletedLotLabels()
    return restored
  },

  /** Findings #183: online only — the server checks both lots and the date before anything is written. */
  moveFishRemote: async (id: string, payload: LotMoveFishCommandPayload): Promise<{ sourceLot: Lot; targetLot: Lot }> => {
    await ensureOfflineReady()

    const remoteResult = await api.lots.moveFish(id, payload)
    await projectLocalLotEvents(remoteResult.events ?? [])
    return { sourceLot: normalizeLot(remoteResult.sourceLot), targetLot: normalizeLot(remoteResult.targetLot) }
  },

  transferRemote: async (id: string, payload: LotTransferCommandPayload): Promise<LotMutationResult<Lot>> => {
    await ensureOfflineReady()

    const remoteResult: LotTransferCommandResult = await api.lots.transfer(id, payload)
    const transferredLot = normalizeLot(remoteResult.transferredLot)

    await dexieLotRepo.upsert(transferredLot)
    await projectLocalLotEvents(remoteResult.events ?? [])
    return buildSyncedLotMutationResult(transferredLot)
  },

  splitRemote: async (id: string, payload: LotSplitCommandPayload): Promise<LotSplitCommandResult> => {
    await ensureOfflineReady()

    const remoteResult = await api.lots.split(id, payload)
    const result: LotSplitCommandResult = {
      createdLots: remoteResult.createdLots.map((lot) => normalizeLot(lot)),
      updatedSourceLot: normalizeLot(remoteResult.updatedSourceLot),
      events: remoteResult.events ?? []
    }

    await dexieLotRepo.upsertMany([result.updatedSourceLot, ...result.createdLots])
    await projectLocalLotEvents(remoteResult.events ?? [])
    return result
  },

  // findings #67 / #71: online-only (one server transaction creates the
  // arrival lot and its per-unit children); the local cache mirrors the result.
  stockIntoUnitsRemote: async (payload: LotStockIntoUnitsPayload): Promise<LotStockIntoUnitsResult> => {
    await ensureOfflineReady()

    const remoteResult = await api.lots.stockIntoUnits(payload)
    const result: LotStockIntoUnitsResult = {
      parentLot: normalizeLot(remoteResult.parentLot),
      childLots: remoteResult.childLots.map((lot) => normalizeLot(lot)),
      events: remoteResult.events ?? []
    }

    await dexieLotRepo.upsertMany([result.parentLot, ...result.childLots])
    await projectLocalLotEvents(remoteResult.events ?? [])
    return result
  },

  mergeRemote: async (payload: LotMergeCommandPayload): Promise<LotMergeCommandResult> => {
    await ensureOfflineReady()

    const remoteResult = await api.lots.merge(payload)
    const result: LotMergeCommandResult = {
      targetLot: normalizeLot(remoteResult.targetLot),
      updatedSourceLots: remoteResult.updatedSourceLots.map((lot) => normalizeLot(lot)),
      events: remoteResult.events ?? []
    }

    await dexieLotRepo.upsertMany([result.targetLot, ...result.updatedSourceLots])
    await projectLocalLotEvents(remoteResult.events ?? [])
    return result
  },

  update: async (id: string, patch: Partial<Omit<Lot, 'id'>>): Promise<LotMutationResult<Lot>> => {
    await ensureOfflineReady()
    const existing = await dexieLotRepo.getById(id)
    if (!existing) throw new Error('Lot not found')

    const nextFarmId = typeof patch.farmId === 'string' && patch.farmId.trim()
      ? patch.farmId.trim()
      : existing.farmId
    const nextProductionUnitId = typeof patch.productionUnitId === 'string' && patch.productionUnitId.trim()
      ? patch.productionUnitId.trim()
      : existing.productionUnitId
    const nextLotKey = typeof patch.lotKey === 'string'
      ? patch.lotKey.trim()
      : existing.lotKey

    if (!nextLotKey) {
      throw new Error('Lot key is required')
    }

    const scopedLots = await dexieLotRepo.list({
      farmId: nextFarmId,
      productionUnitId: nextProductionUnitId
    })
    const duplicate = findCanonicalDuplicateLot({
      lots: scopedLots,
      lotKey: nextLotKey,
      excludeId: id
    })
    if (duplicate) {
      throw new Error(
        `A lot named "${nextLotKey}" already exists in this production unit. Each lot must have a unique name.`
      )
    }

    const next: Lot = normalizeLot({
      ...existing,
      ...patch,
      lineageRootId: typeof patch.lineageRootId === 'string' ? patch.lineageRootId : existing.lineageRootId ?? existing.id,
      sourceLotKey: typeof patch.sourceLotKey === 'string' ? patch.sourceLotKey : existing.sourceLotKey ?? existing.lotKey,
      id: existing.id,
      createdAt: existing.createdAt,
      updatedAt: nowIso()
    })
    const { payload: queuedPayload, projectedEvent } = await buildQueuedLotUpsertProjection({
      nextLot: next,
      previousLot: existing,
      preferredEventType: 'update'
    })

    await dexieLotRepo.upsert(next)
    await projectLocalLotEvents([projectedEvent])
    await enqueueEntityChange({
      entityType: 'lot',
      op: 'upsert',
      entityId: next.id,
      payload: queuedPayload
    })

    void dexieActivityRepo.log({
      source: 'outbox',
      level: 'info',
      title: `Lot updated: ${next.lotKey}`,
      detail: 'Sync queued',
      farmId: next.farmId,
      productionUnitId: next.productionUnitId,
      lotId: next.id,
      dedupeKey: `outbox:lot:upsert:${next.id}`,
      dedupeWindowMs: 30_000
    }).catch(() => {})

    return buildLotMutationResult(next, next.id)
  },

  delete: async (id: string): Promise<LotMutationResult<void>> => {
    resetDeletedLotLabels()
    await ensureOfflineReady()
    const existing = await dexieLotRepo.getById(id)
    await dexieLotRepo.delete(id)
    await pruneUnreachableLotEventCache()
    // Best-effort: also purges this lot's local FeedLog/HarvestLog/
    // LotCostEvent/MortalityLog/WeightSampleLog/FishStockLog rows, which
    // previously leaked forever (see entityCachePurge.ts). Kept separate
    // from the lotEvents prune above, whose failure should still
    // propagate and surface to the operator as today.
    void purgeLocalDataForLot(id)
    await enqueueEntityChange({
      entityType: 'lot',
      op: 'delete',
      entityId: id
    })

    void dexieActivityRepo.log({
      source: 'outbox',
      level: 'warning',
      title: existing?.lotKey ? `Lot deleted: ${existing.lotKey}` : 'Lot deleted',
      detail: 'Sync queued',
      farmId: existing?.farmId,
      productionUnitId: existing?.productionUnitId,
      lotId: id,
      dedupeKey: `outbox:lot:delete:${id}`,
      dedupeWindowMs: 30_000
    }).catch(() => {})

    return buildLotMutationResult(undefined, id)
  },

  clear: async (): Promise<void> => {
    await ensureOfflineReady()
    const existing = await dexieLotRepo.list()
    for (const lot of existing) {
      await enqueueEntityChange({
        entityType: 'lot',
        op: 'delete',
        entityId: lot.id
      })
    }
    await dexieLotRepo.clear()
    await getOfflineDb().lotEvents.clear()
    if (isOnline()) {
      void drainEntityOutboxNow({ maxEntries: 50, maxPasses: 5 })
    }
  }
}
